/**
 * PauseOverlayV2 - Pause overlay component
 *
 * Features:
 * - Full-screen overlay with blur backdrop
 * - "Test Paused" message with coffee icon
 * - Timer showing how long paused
 * - Resume button
 * - Keyboard hint (P or Esc to resume)
 */

import { memo, useState, useEffect } from 'react'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'

// Coffee icon
const CoffeeIcon = () => (
  <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M17 8h1a4 4 0 110 8h-1M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4V8zM6 1v3M10 1v3M14 1v3"
    />
  </svg>
)

// Play icon
const PlayIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const PauseOverlayV2 = memo(() => {
  const isPaused = useChallengeStoreV2((state) => state.isPaused)
  const pausedAt = useChallengeStoreV2((state) => state.pausedAt)
  const resumeChallenge = useChallengeStoreV2((state) => state.resumeChallenge)

  // Track pause duration
  const [pauseDuration, setPauseDuration] = useState(0)

  useEffect(() => {
    if (!isPaused || !pausedAt) {
      setPauseDuration(0)
      return
    }

    // Update pause duration every second
    const interval = setInterval(() => {
      const pauseStart = new Date(pausedAt).getTime()
      const now = Date.now()
      const duration = Math.floor((now - pauseStart) / 1000)
      setPauseDuration(duration)
    }, 1000)

    return () => clearInterval(interval)
  }, [isPaused, pausedAt])

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!isPaused) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-md" />

      {/* Content */}
      <div className="relative z-10 text-center p-8 max-w-md">
        {/* Icon */}
        <div className="flex justify-center mb-6 text-indigo-400">
          <CoffeeIcon />
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-white mb-2">Test Paused</h2>

        {/* Subtitle */}
        <p className="text-gray-400 mb-6">Take a break. Your progress is saved.</p>

        {/* Pause duration */}
        <div className="bg-gray-800/50 rounded-xl px-6 py-4 mb-8 inline-block">
          <span className="text-sm text-gray-400 block mb-1">Paused for</span>
          <span className="text-4xl font-mono font-bold text-white tabular-nums">
            {formatDuration(pauseDuration)}
          </span>
        </div>

        {/* Resume button */}
        <div className="mb-4">
          <button
            onClick={resumeChallenge}
            className="inline-flex items-center gap-2 px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors shadow-lg hover:shadow-xl"
          >
            <PlayIcon />
            Resume
          </button>
        </div>

        {/* Keyboard hint */}
        <p className="text-sm text-gray-500">
          Press{' '}
          <kbd className="px-2 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-xs">P</kbd>{' '}
          or{' '}
          <kbd className="px-2 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-xs">Esc</kbd>{' '}
          to resume
        </p>
      </div>
    </div>
  )
})

PauseOverlayV2.displayName = 'PauseOverlayV2'

export default PauseOverlayV2
